import type { Column } from "@shared/types/data-table"
import type { Refund } from "@shared/types/refunds"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createCurrencyColumn,
  createStatusColumn,
  createPaymentMethodColumn,
  createDateColumn,
  createTruncatedColumn,
  createTextColumn
} from "./column-factories"

export const REFUND_COLUMNS: Record<string, Column<Refund>> = {
  amount: createCurrencyColumn<Refund>("amount", "Amount"),
  status: { ...createStatusColumn<Refund>("refund"), header: "", width: 150 },
  customer: createTruncatedColumn<Refund>(
    "customer",
    "Customer",
    200,
    (refund) => refund.customer?.name || refund.customer?.email || "Unknown",
    "text-muted-foreground group-hover:text-foreground font-normal"
  ),
  reason: createTruncatedColumn<Refund>(
    "reason",
    "Reason",
    180,
    (refund) => refund.reason?.replace(/_/g, " ") || "Unknown",
    "text-muted-foreground group-hover:text-foreground font-normal capitalize"
  ),
  paymentMethod: createPaymentMethodColumn<Refund>(),
  created: createDateColumn<Refund>("created", "Created", 180),
  lastUpdated: createDateColumn<Refund>("lastUpdated", "Last Updated", 150),
  net: createCurrencyColumn<Refund>("net", "Net"),
  fee: createCurrencyColumn<Refund>("fee", "Fee"),
  description: createTextColumn<Refund>("description" as keyof Refund, "Description", 200, "text-muted-foreground group-hover:text-foreground font-normal"),
}

export const REFUND_STATUS_CONFIG = {
  all: ["amount", "status", "customer", "reason", "paymentMethod", "created"],
  pending: ["amount", "status", "customer", "reason", "paymentMethod", "created"],
  succeeded: ["amount", "status", "customer", "reason", "net", "fee", "paymentMethod", "created", "lastUpdated"],
  failed: ["amount", "status", "customer", "reason", "paymentMethod", "created", "lastUpdated"],
  canceled: ["amount", "status", "customer", "reason", "paymentMethod", "created", "lastUpdated"],
} as const

export const getRefundColumns = (status: string = 'all'): Column<Refund>[] => {
  const keys = REFUND_STATUS_CONFIG[status as keyof typeof REFUND_STATUS_CONFIG] || REFUND_STATUS_CONFIG.all
  return keys.map(key => REFUND_COLUMNS[key]).filter(Boolean)
}


export const getRefundColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(REFUND_STATUS_CONFIG[status as keyof typeof REFUND_STATUS_CONFIG] || REFUND_STATUS_CONFIG.all)
  return Object.entries(REFUND_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["amount", "status"].includes(key),
    order: index
  }))
}